import { useEffect, useMemo, useRef, useState } from "react";
import { useLive } from "../../state/use-live.js";
import { storage } from "../../state/storage.js";
import { useUiStore } from "../../state/ui-store.js";
import { AssetImage } from "./AssetImage.js";
import { CharacterEditor } from "./CharacterEditor.js";
import { CharacterTemplateWizard } from "./CharacterTemplateWizard.js";
import { importAssetFromFile, releaseAsset } from "../../platform/assets/index.js";
import { importCharacterCard, exportCharacterCard, deleteCharacter } from "../../state/import-actions.js";
import { useT } from "../../i18n/use-t.js";
import { createChat, createMessage, type Character } from "@roqeet/core";
import { Plus, Sparkles, Upload, X } from "lucide-react";
import { useEscapeToClose } from "../use-escape-to-close.js";

const CHARACTER_RENDER_WINDOW = 120;

/**
 * 캐릭터 라이브러리(결정 #13 반응형 읽기). 아바타는 콘텐츠 주소화 에셋을 lazy
 * objectURL로 표시(결정 #18). 카드 임포트(결정 #6)는 헤더 버튼으로 연결된다.
 *
 * - 카드 클릭: 그 캐릭터로 새 대화를 시작하고 채팅방으로 이동.
 * - 카드 가져오기: JSON/PNG 카드를 골라 캐릭터(+임베드 로어북, PNG면 아바타) 임포트.
 * - 새 캐릭터 / 편집: 캐릭터 에디터로 직접 생성·수정.
 * - 아바타 버튼: 파일을 골라 에셋으로 임포트 후 캐릭터에 연결.
 */
export function CharactersView() {
  const { t } = useT();
  const characters = useLive<Character[]>(() => storage.liveCharacters(), []);
  const setActiveChatId = useUiStore((s) => s.setActiveChatId);
  const setView = useUiStore((s) => s.setView);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ open: boolean; target?: Character }>({ open: false });
  const [templateOpen, setTemplateOpen] = useState(false);
  const [greetingPicker, setGreetingPicker] = useState<{
    character: Character;
    greetings: string[];
  } | null>(null);
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(CHARACTER_RENDER_WINDOW);
  const avatarRef = useRef<HTMLInputElement | null>(null);
  const cardRef = useRef<HTMLInputElement | null>(null);
  const pendingChar = useRef<Character | null>(null);

  // 검색(이름/설명/태그) + 태그 필터(결정 #11 콘텐츠 탐색). 클라이언트 측.
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const c of characters ?? []) for (const t of c.tags) set.add(t);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [characters]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (characters ?? []).filter((c) => {
      if (activeTag && !c.tags.includes(activeTag)) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [characters, query, activeTag]);
  const visibleCharacters = filtered.slice(0, visibleCount);
  const hiddenCount = Math.max(0, filtered.length - visibleCharacters.length);

  useEffect(() => {
    setVisibleCount(CHARACTER_RENDER_WINDOW);
  }, [query, activeTag, characters?.length]);

  // 캐릭터로 새 대화 시작(결정 #22 참가자 모델 + 선택한 인사말, 결정 #16).
  const startChat = async (c: Character, greetingText?: string) => {
    const chat = createChat({ title: t("char.chatTitle", { name: c.name }), characterId: c.id });
    chat.lastMessageAt = Date.now();
    await storage.chats.put(chat);

    const text = greetingText ?? (c.firstMessage || c.alternateGreetings[0] || "");
    if (text) {
      chat.messageCount = 1;
      await storage.chats.put(chat);
      const greeting = createMessage({
        chatId: chat.id,
        senderType: "character",
        characterId: c.id,
        senderName: c.name,
        content: text,
        order: 0,
        isGreeting: true,
      });
      await storage.putMessage(greeting);
    }
    setView("chats");
    setActiveChatId(chat.id);
  };

  /**
   * 대화 시작 트리거. 대체 인사말이 있으면(결정 #16) 어느 인사말로 시작할지
   * 선택 모달을 띄우고, 없으면 바로 시작한다.
   */
  const onStartChat = (c: Character) => {
    const greetings = [c.firstMessage, ...c.alternateGreetings].filter(Boolean);
    if (greetings.length > 1) {
      setGreetingPicker({ character: c, greetings });
    } else {
      void startChat(c);
    }
  };

  const pickAvatar = (c: Character) => {
    pendingChar.current = c;
    avatarRef.current?.click();
  };

  const onAvatarChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const c = pendingChar.current;
    e.target.value = ""; // 같은 파일 재선택 허용
    if (!file || !c) return;
    setBusyId(c.id);
    try {
      const asset = await importAssetFromFile(file);
      // 기존 아바타가 있으면 참조 해제(결정 #18 refCount).
      if (c.avatarAssetId) await releaseAsset(c.avatarAssetId).catch(() => {});
      await storage.characters.put({ ...c, avatarAssetId: asset.id });
    } finally {
      setBusyId(null);
      pendingChar.current = null;
    }
  };

  const onCardChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;
    setImporting(true);
    setNotice(null);
    let ok = 0;
    const warnings: string[] = [];
    for (const file of files) {
      try {
        const { character, unmappedKeys } = await importCharacterCard(file, file.name);
        ok++;
        if (unmappedKeys.length) {
          warnings.push(t("char.unmappedFields", { name: character.name, count: unmappedKeys.length }));
        }
      } catch (err) {
        warnings.push(`${file.name}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    setImporting(false);
    setNotice(
      [t("char.cardsImported", { count: ok }), ...warnings].join(" / ").slice(0, 300),
    );
  };

  return (
    <div className="flex h-full flex-col">
      <input
        ref={avatarRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onAvatarChosen}
      />
      <input
        ref={cardRef}
        type="file"
        accept="image/png,application/json,.json,.png,.charx"
        multiple
        className="hidden"
        onChange={onCardChosen}
      />

      <div className="flex items-center justify-between gap-2 border-b border-line p-3">
        <span className="text-sm text-ink-soft">
          {characters ? t("char.characterCount", { count: characters.length }) : t("common.loading")}
        </span>
        <div className="flex gap-2">
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised disabled:opacity-50"
            onClick={() => cardRef.current?.click()}
            disabled={importing}
            title={t("char.importTitle")}
          >
            <Upload size={16} aria-hidden />
            {importing ? t("char.importing") : t("char.importCard")}
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={() => setTemplateOpen(true)}
          >
            <Sparkles size={16} aria-hidden />
            템플릿
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft"
            onClick={() => setEditor({ open: true })}
          >
            <Plus size={16} aria-hidden />
            {t("char.newCharacter")}
          </button>
        </div>
      </div>

      {notice && (
        <div className="flex items-start justify-between gap-2 border-b border-line bg-bg-soft px-3 py-2 text-xs text-ink-soft">
          <span className="min-w-0 break-words">{notice}</span>
          <button
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-faint hover:bg-bg-raised hover:text-ink"
            onClick={() => setNotice(null)}
            aria-label={t("misc.close")}
            title={t("misc.close")}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* 검색 + 태그 필터(결정 #11 콘텐츠 탐색). */}
      {(characters?.length ?? 0) > 0 && (
        <div className="space-y-2 border-b border-line p-3">
          <input
            className="w-full rounded-lg border border-line bg-bg-soft px-3 py-2 text-sm text-ink outline-none focus:border-accent"
            placeholder={t("char.searchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <button
                className={[
                  "rounded-full px-2 py-0.5 text-xs",
                  activeTag === null ? "bg-accent text-white" : "bg-bg-raised text-ink-soft hover:bg-bg-soft",
                ].join(" ")}
                onClick={() => setActiveTag(null)}
              >
                {t("char.allTags")}
              </button>
              {allTags.map((t) => (
                <button
                  key={t}
                  className={[
                    "rounded-full px-2 py-0.5 text-xs",
                    activeTag === t ? "bg-accent text-white" : "bg-bg-raised text-ink-soft hover:bg-bg-soft",
                  ].join(" ")}
                  onClick={() => setActiveTag((cur) => (cur === t ? null : t))}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-3 scroll-touch">
        {characters?.length === 0 ? (
          <p className="px-1 py-8 text-center text-sm text-ink-faint">
            {t("char.emptyNoChars")}
          </p>
        ) : filtered.length === 0 ? (
          <p className="px-1 py-8 text-center text-sm text-ink-faint">
            {t("char.emptyNoResults")}
          </p>
        ) : (
          <>
          <ul
            className="grid gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))" }}
          >
            {visibleCharacters.map((c) => (
              <li
                key={c.id}
                className="flex flex-col rounded-xl border border-line bg-bg-soft p-3"
              >
                <button
                  className="group relative mb-2 aspect-square w-full overflow-hidden rounded-lg"
                  onClick={() => onStartChat(c)}
                  title={t("char.startChatWith", { name: c.name })}
                >
                  <AssetImage
                    assetId={c.avatarAssetId}
                    alt={c.name}
                    className="h-full w-full object-cover"
                    fallback={<span className="text-2xl font-bold">{c.name.slice(0, 1)}</span>}
                  />
                  <span className="absolute inset-x-0 bottom-0 bg-black/50 py-1 text-center text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                    {t("char.startChat")}
                  </span>
                </button>
                <span className="truncate text-sm text-ink">{c.name}</span>
                <span className="truncate text-xs text-ink-faint">
                  {c.tags.length ? c.tags.join(", ") : t("char.noTags")}
                </span>
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  <button
                    className="h-8 rounded-lg border border-line px-2 text-xs text-ink-soft hover:bg-bg-raised whitespace-nowrap"
                    onClick={() => setEditor({ open: true, target: c })}
                  >
                    {t("common.edit")}
                  </button>
                  <button
                    className="h-8 rounded-lg border border-line px-2 text-xs text-ink-soft hover:bg-bg-raised disabled:opacity-50 whitespace-nowrap"
                    onClick={() => pickAvatar(c)}
                    disabled={busyId === c.id}
                  >
                    {busyId === c.id ? t("char.uploading") : t("char.avatar")}
                  </button>
                  <button
                    className="h-8 rounded-lg border border-line px-2 text-xs text-ink-soft hover:bg-bg-raised whitespace-nowrap"
                    onClick={() => exportCharacterCard(c)}
                    title={t("char.exportTitle")}
                  >
                    {t("common.export")}
                  </button>
                  <button
                    className="h-8 rounded-lg border border-line px-2 text-xs text-red-300 hover:bg-bg-raised whitespace-nowrap"
                    onClick={() => {
                      if (confirm(t("char.deleteConfirm", { name: c.name }))) void deleteCharacter(c);
                    }}
                  >
                    {t("common.delete")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <div className="mt-3 flex justify-center">
              <button
                className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised"
                onClick={() => setVisibleCount((count) => count + CHARACTER_RENDER_WINDOW)}
              >
                {t("chat.loadMore", { count: Math.min(CHARACTER_RENDER_WINDOW, hiddenCount), hidden: hiddenCount })}
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {editor.open && (
        <CharacterEditor
          {...(editor.target ? { character: editor.target } : {})}
          onClose={() => setEditor({ open: false })}
        />
      )}

      {templateOpen && (
        <CharacterTemplateWizard
          onClose={() => setTemplateOpen(false)}
          onCreated={async (character, shouldStartChat) => {
            if (shouldStartChat) await startChat(character);
            else setNotice(`${character.name} 캐릭터를 만들었습니다.`);
          }}
        />
      )}

      {greetingPicker && (
        <GreetingPicker
          character={greetingPicker.character}
          greetings={greetingPicker.greetings}
          onPick={(text) => {
            const c = greetingPicker.character;
            setGreetingPicker(null);
            void startChat(c, text);
          }}
          onCancel={() => setGreetingPicker(null)}
        />
      )}
    </div>
  );
}

/**
 * 인사말 선택 모달(결정 #16: 다중 인사말). 캐릭터에 first_mes + 대체 인사말이
 * 여럿일 때 어느 것으로 대화를 시작할지 고른다.
 */
function GreetingPicker({
  character,
  greetings,
  onPick,
  onCancel,
}: {
  character: Character;
  greetings: string[];
  onPick: (text: string) => void;
  onCancel: () => void;
}) {
  const { t } = useT();
  useEscapeToClose(onCancel);
  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="flex max-h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col rounded-xl border border-line bg-bg-soft"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="text-sm font-semibold text-ink">{t("char.greetingPickerTitle", { name: character.name })}</span>
          <button
            className="flex h-8 w-8 items-center justify-center rounded-md text-ink-faint hover:bg-bg-raised hover:text-ink"
            onClick={onCancel}
            aria-label={t("char.close")}
            title={t("char.close")}
          >
            <X size={17} aria-hidden="true" />
          </button>
        </div>
        <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3 scroll-touch">
          {greetings.map((g, i) => (
            <li key={i}>
              <button
                className="w-full rounded-lg border border-line bg-bg-raised px-3 py-2 text-left text-sm text-ink hover:border-accent"
                onClick={() => onPick(g)}
              >
                <span className="mb-1 block text-xs text-ink-faint">
                  {i === 0 ? t("char.defaultGreeting") : t("char.altGreeting", { n: i })}
                </span>
                <span className="line-clamp-4 whitespace-pre-wrap break-words">{g}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
